import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getSettings, updateSettings, changePassword, regenerateApiKey, revokeApiKey, downloadApiDocs } from "@/lib/api";
import { Save, Loader2, Mail, Shield, Eye, EyeOff, Server, Key, Copy, RefreshCw, Trash2, FileText, Download } from "lucide-react";

export default function SettingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiKeyCreatedAt, setApiKeyCreatedAt] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_use_tls: true,
    default_recipient: "",
  });

  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings({
        smtp_host: response.data.smtp_host || "",
        smtp_port: response.data.smtp_port || 587,
        smtp_user: response.data.smtp_user || "",
        smtp_password: response.data.smtp_password || "",
        smtp_use_tls: response.data.smtp_use_tls ?? true,
        default_recipient: response.data.default_recipient || "",
      });
      setApiKey(response.data.api_key || "");
      setApiKeyCreatedAt(response.data.api_key_created_at || "");
    } catch (error) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new) {
      toast.error("Bitte füllen Sie alle Felder aus");
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    if (passwordData.new.length < 6) {
      toast.error("Das neue Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(passwordData.current, passwordData.new);
      toast.success("Passwort geändert");
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Passwort konnte nicht geändert werden");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleRotateApiKey = async () => {
    setIsRotating(true);
    try {
      const response = await regenerateApiKey();
      setApiKey(response.data.api_key);
      setApiKeyCreatedAt(response.data.created_at);
      setShowApiKey(true);
      toast.success(apiKey ? "Neuer API-Key generiert — alter Key ist jetzt ungültig" : "API-Key generiert");
    } catch (error) {
      toast.error("Fehler beim Generieren: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsRotating(false);
      setConfirmRotate(false);
    }
  };

  const handleRevokeApiKey = async () => {
    try {
      await revokeApiKey();
      setApiKey("");
      setApiKeyCreatedAt("");
      setShowApiKey(false);
      toast.success("API-Key widerrufen");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setConfirmRevoke(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const handleDownloadDocs = async (lang) => {
    try {
      const response = await downloadApiDocs(lang, window.location.origin);
      const blob = new Blob([response.data], { type: "text/markdown;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        lang === "de" ? "sanza-api-dokumentation-de.md" : "sanza-api-documentation-en.md";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === "de" ? "Dokumentation (DE) heruntergeladen" : "Documentation (EN) downloaded");
    } catch (error) {
      toast.error("Download fehlgeschlagen: " + (error.response?.data?.detail || error.message));
    }
  };

  const apiBaseUrl = `${window.location.origin}/api/public/v1`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Einstellungen
        </h1>
        <p className="text-slate-500 mt-1">Konfigurieren Sie E-Mail und Sicherheit</p>
      </div>

      {/* Email Settings */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                E-Mail-Einstellungen
              </CardTitle>
              <CardDescription>SMTP-Server für den E-Mail-Versand</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_host">SMTP-Server</Label>
              <Input
                id="smtp_host"
                data-testid="input-smtp-host"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.beispiel.de"
                className="border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_port">Port</Label>
              <Input
                id="smtp_port"
                data-testid="input-smtp-port"
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                placeholder="587"
                className="border-slate-200"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_user">Benutzername</Label>
              <Input
                id="smtp_user"
                data-testid="input-smtp-user"
                value={settings.smtp_user}
                onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                placeholder="benutzer@beispiel.de"
                className="border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_password">Passwort</Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  data-testid="input-smtp-password"
                  type={showSmtpPassword ? "text" : "password"}
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  className="border-slate-200 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">TLS/SSL verwenden</p>
                <p className="text-sm text-slate-500">Sichere Verbindung zum Mail-Server</p>
              </div>
            </div>
            <Switch
              data-testid="switch-smtp-tls"
              checked={settings.smtp_use_tls}
              onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked })}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="default_recipient">Standard-Empfänger</Label>
            <Input
              id="default_recipient"
              data-testid="input-default-recipient"
              type="email"
              value={settings.default_recipient}
              onChange={(e) => setSettings({ ...settings, default_recipient: e.target.value })}
              placeholder="standard@empfaenger.de"
              className="border-slate-200"
            />
            <p className="text-xs text-slate-500">
              Diese E-Mail-Adresse wird beim Senden automatisch vorgeschlagen.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              data-testid="save-settings-button"
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Einstellungen speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Passwort ändern
              </CardTitle>
              <CardDescription>Ändern Sie Ihr Anmeldepasswort</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Aktuelles Passwort</Label>
            <Input
              id="current_password"
              data-testid="input-current-password"
              type="password"
              value={passwordData.current}
              onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
              placeholder="••••••••"
              className="border-slate-200"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">Neues Passwort</Label>
              <Input
                id="new_password"
                data-testid="input-new-password"
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                placeholder="••••••••"
                className="border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Passwort bestätigen</Label>
              <Input
                id="confirm_password"
                data-testid="input-confirm-password"
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                placeholder="••••••••"
                className="border-slate-200"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              data-testid="change-password-button"
              onClick={handleChangePassword}
              disabled={isChangingPassword}
              variant="outline"
              className="border-slate-200"
            >
              {isChangingPassword ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Passwort ändern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Hinweis zur E-Mail-Konfiguration</p>
              <p className="mt-1">
                Für Gmail verwenden Sie: smtp.gmail.com, Port 587 mit TLS. 
                Sie benötigen möglicherweise ein App-spezifisches Passwort.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Öffentliche API Card */}
      <Card className="border-slate-200" data-testid="api-key-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle>Öffentliche API (für Kundensoftware)</CardTitle>
              <CardDescription>
                Rein lesender Zugriff auf Bestand & Buchungen per API-Key.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>API-Key</Label>
            {apiKey ? (
              <>
                <div className="relative">
                  <Input
                    data-testid="input-api-key"
                    type={showApiKey ? "text" : "password"}
                    value={apiKey}
                    readOnly
                    className="border-slate-200 pr-24 font-mono text-xs"
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="p-1.5 text-slate-400 hover:text-slate-600"
                      title={showApiKey ? "Ausblenden" : "Anzeigen"}
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(apiKey, "API-Key")}
                      className="p-1.5 text-slate-400 hover:text-slate-600"
                      title="Kopieren"
                      data-testid="copy-api-key"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {apiKeyCreatedAt && (
                  <p className="text-xs text-slate-500">
                    Erstellt am {new Date(apiKeyCreatedAt).toLocaleString("de-DE")}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 italic">
                Noch kein API-Key vorhanden. Klicken Sie unten auf „Generieren".
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => (apiKey ? setConfirmRotate(true) : handleRotateApiKey())}
              disabled={isRotating}
              className="border-slate-200"
              data-testid="btn-rotate-api-key"
            >
              {isRotating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {apiKey ? "Neuen Key generieren" : "Generieren"}
            </Button>
            {apiKey && (
              <Button
                variant="outline"
                onClick={() => setConfirmRevoke(true)}
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="btn-revoke-api-key"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Widerrufen
              </Button>
            )}
          </div>

          <Separator />

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-700">Endpunkte</h4>
            <div className="space-y-3 text-xs">
              <div className="p-3 bg-slate-50 rounded border border-slate-200 font-mono">
                <div className="flex items-center justify-between gap-2">
                  <span><span className="text-emerald-600 font-semibold">GET</span> {apiBaseUrl}/inventory</span>
                  <button
                    onClick={() => copyToClipboard(`curl -H "X-API-Key: ${apiKey || "<IHR_API_KEY>"}" ${apiBaseUrl}/inventory`, "curl-Befehl")}
                    className="text-slate-400 hover:text-slate-600"
                    title="curl kopieren"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <p className="mt-1 text-slate-500 font-sans">Aktueller Bestand aller Produkte</p>
              </div>
              <div className="p-3 bg-slate-50 rounded border border-slate-200 font-mono">
                <div className="flex items-center justify-between gap-2">
                  <span><span className="text-emerald-600 font-semibold">GET</span> {apiBaseUrl}/inventory/transactions?limit=100</span>
                  <button
                    onClick={() => copyToClipboard(`curl -H "X-API-Key: ${apiKey || "<IHR_API_KEY>"}" "${apiBaseUrl}/inventory/transactions?limit=100"`, "curl-Befehl")}
                    className="text-slate-400 hover:text-slate-600"
                    title="curl kopieren"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <p className="mt-1 text-slate-500 font-sans">Buchungshistorie (optional: <code className="px-1 bg-slate-200 rounded">product_code</code>-Filter, max 500)</p>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded border border-blue-100 text-xs text-blue-800">
              <p className="font-medium mb-1">Beispiel-Request</p>
              <code className="block whitespace-pre-wrap font-mono text-[11px]">
                {`curl -H "X-API-Key: ${apiKey ? apiKey.slice(0, 12) + "…" : "<IHR_API_KEY>"}" \\
     ${apiBaseUrl}/inventory`}
              </code>
            </div>

            <div className="p-4 bg-emerald-50 rounded border border-emerald-200">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-900">Integrations-Dokumentation für Ihren Kunden</p>
                  <p className="text-xs text-emerald-800 mt-0.5 mb-3">
                    Markdown-Datei mit allen Endpunkten, Code-Beispielen (curl, Python, JS) und Best Practices — bereit zum Weitergeben.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadDocs("de")}
                      className="border-emerald-300 bg-white hover:bg-emerald-50"
                      data-testid="btn-download-docs-de"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Dokumentation (Deutsch)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadDocs("en")}
                      className="border-emerald-300 bg-white hover:bg-emerald-50"
                      data-testid="btn-download-docs-en"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Documentation (English)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Neuen API-Key generieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Der bisherige Key wird <strong>sofort ungültig</strong>. Jede Software, die ihn nutzt, muss den neuen Key eintragen, sonst bekommt sie <code>401 Ungültiger API-Key</code>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRotateApiKey} className="bg-blue-600 hover:bg-blue-700">
              Trotzdem neu generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>API-Key widerrufen?</AlertDialogTitle>
            <AlertDialogDescription>
              Nach dem Widerruf funktioniert die API nicht mehr, bis Sie einen neuen Key generieren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeApiKey} className="bg-red-600 hover:bg-red-700">
              Widerrufen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
